// Fetch + shape de dades d'UN widget. Extret del cos del map intern de
// Dashboard.fetchData perquè el comparteixin el dashboard i el preview de l'editor.
// Encapsula: resoldre noms de dimensió → IDs (via metadata del segment), cridar
// getDashboardStats, filtrar client-side, injectar la clau acumulada i transformar.
// NO sap res de drill/cross-filter: el filtre i el flag cumulative entren per arguments
// (el dashboard els munta i els passa; el preview passa {} i false).

import { ref } from 'vue';
import { dashboardService } from '../services/dashboardService';
import { tConfig } from '../i18n/index.js';
import { transformApiData, CUMULATIVE_KEY, CUMULATIVE_SEP } from '../utils/widgetTransform.js';
import { debugLog } from '../utils/debug.js';

/**
 * @param {{lang?: string}} ctx idioma actiu (per a missingLabel/presence/títol de sèrie)
 */
export function useWidgetData({ lang = 'ca_es' } = {}) {
  // Per segment: mapa indexField-net (minúscules) → id de dimensió.
  const metadataCache = ref({});
  // Per segment: mapa indexField-net (minúscules) → label del títol resolt pels serveis.
  const metaLabelCache = ref({});

  /**
   * Assegura (i cacheja) el metadata d'un segment. Retorna el dimMap; propaga l'error
   * al cridador (el dashboard hi aplica detectApiError; el preview el mostra com a nota).
   * @param {string} segmentId
   */
  async function ensureMetadata(segmentId) {
    if (metadataCache.value[segmentId]) return metadataCache.value[segmentId];

    debugLog(`Discovering metadata for segment: ${segmentId}...`);
    const metadata = await dashboardService.getSegmentMetadata(segmentId);
    const rawList = metadata?.dimensionsList || metadata?.data?.dimensionsList || [];

    const dimMap = {};
    const labelByKey = {};
    rawList.forEach(dim => {
      if (dim.indexField && dim.id) {
        const cleanKey = dim.indexField.replace(/[\[\]\" ]/g, '');
        dimMap[cleanKey.toLowerCase()] = dim.id;
        if (dim.label) labelByKey[cleanKey.toLowerCase()] = dim.label;
      }
    });

    metadataCache.value[segmentId] = dimMap;
    metaLabelCache.value[segmentId] = labelByKey;
    return dimMap;
  }

  /**
   * Fetch + transform d'un widget JA efectiu.
   * @param {object} widget widget efectiu (getEffectiveWidget aplicat pel cridador)
   * @param {object} opts
   * @param {object|null} [opts.filters] mapa { camp: valor } a aplicar (dimensió + filtratge)
   * @param {boolean} [opts.cumulative] injecta la clau acumulada (mode cumulative)
   * @return {Promise<object|null>} node de dades transformat (o null si falta segment)
   */
  async function fetchWidget(widget, { filters = null, cumulative = false } = {}) {
    if (!widget?.params?.segmentId) return null;

    let jsonData = widget.params.jsonData;
    const dimensionParam = widget.params.dimension;
    const baseDimNames = Array.isArray(dimensionParam) ? dimensionParam : (dimensionParam ? [dimensionParam] : []);

    const activeFilter = (filters && Object.keys(filters).length) ? filters : null;
    const filterFields = activeFilter ? Object.keys(activeFilter) : [];
    const dimensionNames = filterFields.length
      ? [...filterFields, ...baseDimNames.filter(d => !filterFields.includes(d))]
      : baseDimNames;

    if (dimensionNames.length > 0) {
      const dimMap = metadataCache.value[widget.params.segmentId] || {};
      const resolvedIds = dimensionNames
        .map(name => dimMap[name.toLowerCase().trim()])
        .filter(id => !!id);
      if (resolvedIds.length > 0) {
        jsonData = resolvedIds;
        debugLog(`Resolved dimensions [${dimensionNames}] to`, jsonData);
      }
    }

    const rawData = await dashboardService.getDashboardStats(widget.params.segmentId, jsonData, {
      includeMissing: !!widget.params.includeMissing,
      missingLabel: widget.params.includeMissing && widget.params.missingLabel
        ? tConfig(widget.params.missingLabel, lang)
        : undefined,
      metrics: widget.params.metrics,
    });

    const normalize = s => String(s).toLowerCase().replace(/[._\[\]"= ]/g, '');
    const findKey = (name, keys) => {
      const norm = normalize(name);
      return keys.find(k => normalize(k) === norm)
        || keys.find(k => normalize(k).includes(norm))
        || name;
    };

    if (activeFilter && Array.isArray(rawData?.data)) {
      const dataKeys = rawData.data[0] ? Object.keys(rawData.data[0]) : [];
      rawData.data = rawData.data.filter(item => {
        return Object.entries(activeFilter).every(([field, value]) => {
          const matchKey = findKey(field, dataKeys);
          return String(item[matchKey]) === String(value);
        });
      });
    }

    if (cumulative && Array.isArray(rawData?.data) && rawData.data.length) {
      const dataKeys = Object.keys(rawData.data[0]);
      const activeDims = Array.isArray(widget.params.dimension) ? widget.params.dimension : [widget.params.dimension];
      const dimKeys = activeDims.map(d => findKey(d, dataKeys));
      rawData.data.forEach(item => {
        item[CUMULATIVE_KEY] = dimKeys.map(k => item[k] ?? '?').join(CUMULATIVE_SEP);
      });
    }

    const node = transformApiData(rawData, widget, { lang });
    if (node && typeof node === 'object') {
      node.domainLabels = rawData?.domainLabels || {};
      node.metricTotals = rawData?.metricTotals || {};
    }
    return node;
  }

  return { metadataCache, metaLabelCache, ensureMetadata, fetchWidget };
}
